/* global publish, PUB_SUB_EVENTS, APPSTLE_PROPS, SHOPIFY_PREFIX, subscribe routes ReportSentry CartPerformance HTMLUpdateUtility fetchShopifyGraphQL nanoid */
/**
 * @description parse or generate section id
 * @class SectionId
 * @static
 * @method parseId
 * @method parseSectionName
 * @method getIdForSection
 */
class SectionId {
  static #separator = '__'

  /**
   * @description ex => section id: 'template--22224696705326__main' => 'template--22224696705326'
   * @param {string} qualifiedSectionId
   * @returns {string}
   */
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0]
  }

  /**
   * @description ex => section name: 'template--22224696705326__main' => 'main'
   * @param {string} qualifiedSectionId
   * @returns {string}
   */
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1]
  }

  /**
   * @description generate section id by id and name
   * @param {string} sectionId
   * @param {string} sectionName
   * @returns {string}
   */
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`
  }
}

const CREATE_CART_GQL = `mutation CartCreate($cartInput: CartInput!) {
  cartCreate(input: $cartInput) {
    cart {
      id
      checkoutUrl
      lines(first: 50){
        nodes {
          id
          attributes { key value }
          merchandise {
            ... on ProductVariant {
              id
            }
          }
        }
      }
    }
    userErrors {
      field
      message
    }
  }
}`

const ADD_CART_LINES_GQL = `mutation CartAddLines($cartId: ID!, $lines: [CartLineInput!]!) {
  cartLinesAdd(cartId: $cartId, lines: $lines) {
    cart {
      id
      checkoutUrl
    }
  }
}`

class ProductForm extends HTMLElement {
  constructor() {
    super()

    this.form = this.querySelector('form')
    if (this.variantIdInput) this.variantIdInput.disabled = false
    this.onSubmitHandlerBound = this.onSubmitHandler.bind(this)
    this.form?.addEventListener('submit', this.onSubmitHandlerBound)
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer')
    this.submitButton = this.querySelector('.product-form-submit-button')
    this.submitButtonText = this.submitButton?.querySelector('span')
    this.buyNowButton = this.querySelector('.product-form-payment-button')
    this.subscribeRadio = null

    this.hideErrors = this.dataset.hideErrors === 'true'
  }

  connectedCallback() {
    this.buyNowButton.classList.remove('loading')
    this.subscribeRadio = document.querySelector('product-filter-subscription-radio')
  }

  reportSubscriptionWdEvent(eventName, selectedFilterSellingPlans) {
    try {
      if (!selectedFilterSellingPlans.length || typeof window.WDSendEvent !== 'function') return

      const wdEvent = this.subscribeRadio?.dataset?.wdEvent ?? '{}'
      const { event_action: eventAction, event_label: eventLabel = {} } = JSON.parse(wdEvent)
      const skus = selectedFilterSellingPlans
        .map(plan => plan.split('|')[0])
        .filter(id => eventLabel[id])
        .map(id => eventLabel[id])

      window.WDSendEvent(eventName, {
        event_source: 'pdp-info',
        event_action: eventAction,
        event_label: skus.join(',')
      })
    } catch (error) {
      console.error(error)
    }
  }

  async buyNowHandler(event, formData) {
    const selectedFilterSellingPlans = formData.getAll('filter_selling_plan[]').filter(Boolean) || []
    const hasFilterSellingPlan =
      formData.get('purchase_type') === 'subscription' && selectedFilterSellingPlans.length > 0
    const lineItemProperties =
      this.dataset.isGiftCard === 'true'
        ? Array.from(formData.entries()).reduce((attributes, [name, value]) => {
            const propertyMatch = name.match(/^properties\[(.+)\]$/)
            if (propertyMatch && typeof value === 'string' && value.trim()) {
              attributes.push({ key: propertyMatch[1], value })
            }
            return attributes
          }, [])
        : []
    event.submitter.classList.add('loading')
    const eventCategory = event.submitter.getAttribute('data-event-category') || ''
    if (typeof window.WDSendEvent === 'function') {
      window.WDSendEvent('wd_buy_now', {
        event_source: window.location.pathname,
        event_category: eventCategory,
        event_action: event.submitter.getAttribute('data-sku') || ''
      })
    }

    if (formData.has('bundle_ids') && !hasFilterSellingPlan) {
      const lines = this.buildBundleCartItems(formData).map(item => ({
        quantity: item.quantity,
        merchandiseId: `${SHOPIFY_PREFIX.variant}${item.id}`,
        ...(item.selling_plan ? { sellingPlanId: `${SHOPIFY_PREFIX.sellingPlan}${item.selling_plan}` } : {}),
        attributes: Object.entries(item.properties).map(([key, value]) => ({
          key,
          value: value == null ? '' : String(value)
        }))
      }))
      const cartRes = await fetchShopifyGraphQL({
        query: CREATE_CART_GQL,
        variables: { cartInput: { lines } }
      })
      event.submitter.classList.remove('loading')
      window.location.href = cartRes.data?.cartCreate?.cart?.checkoutUrl || '/checkout'
      return
    }

    const sellingPlan = formData.get('selling_plan') || ''

    const parsedQty = Number.parseInt(formData.get('quantity'), 10)
    const quantity = Number.isFinite(parsedQty) && parsedQty > 0 ? parsedQty : 1
    //- 正常单体购买
    const variables = {
      cartInput: {
        lines: [
          {
            quantity,
            merchandiseId: `${SHOPIFY_PREFIX.variant}${formData.get('id')}`,
            ...(sellingPlan ? { sellingPlanId: `${SHOPIFY_PREFIX.sellingPlan}${formData.get('selling_plan')}` } : {}),
            ...(lineItemProperties.length || sellingPlan
              ? {
                  attributes: [
                    ...lineItemProperties,
                    ...(sellingPlan
                      ? [
                          { key: APPSTLE_PROPS.SELECTED_PLAN, value: formData.get('selling_plan') },
                          { key: APPSTLE_PROPS.FULFILLMENT_COUNT, value: '1' }
                        ]
                      : [])
                  ]
                }
              : {})
          }
        ]
      }
    }
    if (!hasFilterSellingPlan) {
      const cartRes = await fetchShopifyGraphQL({ query: CREATE_CART_GQL, variables })
      event.submitter.classList.remove('loading')
      const checkoutUrl = cartRes.data?.cartCreate?.cart?.checkoutUrl || '/checkout'
      window.location.href = checkoutUrl
      return
    }
    //- 整机捆绑购买
    variables.cartInput.lines = []
    for (let i = 0; i < quantity; i++) {
      const relatedId = nanoid(6)
      variables.cartInput.lines.push({
        quantity: 1,
        merchandiseId: `${SHOPIFY_PREFIX.variant}${formData.get('id')}`,
        attributes: [...lineItemProperties, { key: '__related', value: relatedId }]
      })
    }
    //* 先创建购物车并添加整机
    const cartRes = await fetchShopifyGraphQL({ query: CREATE_CART_GQL, variables })
    const lines = cartRes.data?.cartCreate?.cart?.lines?.nodes || []
    const children = []
    //* 遍历整机为每个整机添加配套的滤芯订购计划
    for (const line of lines) {
      const { value } = line.attributes.find(attr => attr.key === '__related')
      for (let j = selectedFilterSellingPlans.length - 1; j >= 0; j--) {
        const filterItem = selectedFilterSellingPlans[j]
        const [sellingPlan, filterId, perDelivery] = filterItem.split('|').map(part => part.trim())
        if (!sellingPlan || !filterId) continue
        children.push({
          quantity: 1,
          merchandiseId: `${SHOPIFY_PREFIX.variant}${filterId}`,
          sellingPlanId: `${SHOPIFY_PREFIX.sellingPlan}${sellingPlan}`,
          parent: { lineId: line.id },
          attributes: [
            { key: APPSTLE_PROPS.SELECTED_PLAN, value: sellingPlan },
            { key: APPSTLE_PROPS.FULFILLMENT_COUNT, value: '1' },
            { key: '__related', value },
            { key: '__per_delivery', value: perDelivery }
          ]
        })
      }
    }
    if (!cartRes.data?.cartCreate?.cart?.id) return
    await fetchShopifyGraphQL({
      query: ADD_CART_LINES_GQL,
      variables: { cartId: cartRes.data?.cartCreate?.cart?.id, lines: children }
    })
    event.submitter.classList.remove('loading')
    const checkoutUrl = cartRes.data?.cartCreate?.cart?.checkoutUrl || '/checkout'
    if (hasFilterSellingPlan) {
      //- 整机订购省埋点上报
      this.reportSubscriptionWdEvent('wd_buynow_subscription', selectedFilterSellingPlans)
    }
    window.location.href = checkoutUrl
  }

  appendCartSections(formData) {
    if (!this.cart || typeof this.cart.getSectionsToRender !== 'function') return

    formData.append(
      'sections',
      this.cart.getSectionsToRender()?.map(section => section.id)
    )
    formData.append('sections_url', window.location.pathname)
  }

  buildBundleCartItems(formData) {
    const bundlesSellingPlan = formData.get('bundle_selling_plan')
    const selectedPlanPrice = bundlesSellingPlan
      ? this.querySelector(`input[name="bundle_selling_plan"][value="${bundlesSellingPlan}"]`)?.dataset.bundlePlanPrice
      : ''
    const bundlePrice = selectedPlanPrice || formData.get('bundle_price')
    const quantity = Number.parseInt(formData.get('quantity'), 10) || 1
    const bundleIds = formData.get('bundle_ids')

    return bundleIds.split(';').map(bundle => {
      const [variantId, bundleQuantity] = bundle.split(':')

      return {
        quantity: (Number.parseInt(bundleQuantity, 10) || 1) * quantity,
        id: variantId,
        properties: {
          'Part Of': formData.get('bundle_title').replace('||', ' '),
          __is_bundle: bundlesSellingPlan ? `${bundleIds};${bundlesSellingPlan}` : bundleIds,
          __bundle_img: formData.get('bundle_img'),
          __bundle_url: formData.get('bundle_url'),
          __bundle_title: formData.get('bundle_title'),
          __bundle_price: bundlePrice,
          ...(bundlesSellingPlan
            ? { [APPSTLE_PROPS.SELECTED_PLAN]: bundlesSellingPlan, [APPSTLE_PROPS.FULFILLMENT_COUNT]: '1' }
            : {})
        },
        ...(bundlesSellingPlan ? { selling_plan: bundlesSellingPlan } : {})
      }
    })
  }

  buildFilterSubscriptionCartItems(formData, selectedFilterSellingPlans) {
    const quantity = Math.max(1, parseInt(formData.get('quantity'), 10) || 1)
    const id = formData.get('id')
    const items = []

    for (let i = 0; i < quantity; i += 1) {
      const relatedId = nanoid(6)
      for (let j = selectedFilterSellingPlans.length - 1; j >= 0; j -= 1) {
        const [sellingPlan, filterId, perDelivery] = selectedFilterSellingPlans[j].split('|').map(part => part.trim())
        if (!sellingPlan || !filterId) continue
        items.push({
          quantity: 1,
          id: filterId,
          selling_plan: sellingPlan,
          parent_id: id,
          properties: {
            [APPSTLE_PROPS.SELECTED_PLAN]: sellingPlan,
            [APPSTLE_PROPS.FULFILLMENT_COUNT]: '1',
            __related: relatedId,
            __per_delivery: perDelivery
          }
        })
      }
      items.push({ quantity: 1, id, properties: { __related: relatedId } })
    }

    return items
  }

  /**
   * @description override form submit event: add to cart
   * @param {Event} event
   */
  async onSubmitHandler(event) {
    event.preventDefault()
    if (
      event.submitter?.id !== this.buyNowButton?.id &&
      event.submitter?.id !== this.submitButton?.id &&
      !event.submitter?.classList.contains('foxkit-preorder-submit')
    ) {
      return
    }
    const formData = new FormData(this.form)

    if (event.submitter.id === this.buyNowButton.id) {
      try {
        await this.buyNowHandler(event, formData)
      } catch (error) {
        event.submitter.classList.remove('loading')
        console.error(error)
      }
      return
    }

    // - add to cart
    if (event.submitter.getAttribute('aria-disabled') === 'true') return

    this.handleErrorMessage()

    //- update submit button state
    event.submitter.setAttribute('aria-disabled', true)
    event.submitter.classList.add('loading')

    const selectedFilterSellingPlans = formData.getAll('filter_selling_plan[]').filter(Boolean) || []
    const hasFilterSellingPlan =
      formData.get('purchase_type') === 'subscription' && selectedFilterSellingPlans.length > 0
    const isBundles = formData.has('bundle_ids')
    const useJsonCart = hasFilterSellingPlan || isBundles
    const url = useJsonCart ? `${routes.cart_add_url}.js` : routes.cart_add_url
    const headers = useJsonCart
      ? { 'Content-Type': 'application/json' }
      : { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' }

    this.appendCartSections(formData)
    const variables = { sections: formData.get('sections') }

    if (isBundles) variables.items = this.buildBundleCartItems(formData)
    if (hasFilterSellingPlan) {
      variables.items = this.buildFilterSubscriptionCartItems(formData, selectedFilterSellingPlans)
    } else if (formData.has('selling_plan') && formData.get('selling_plan')) {
      formData.append(`properties[${APPSTLE_PROPS.SELECTED_PLAN}]`, formData.get('selling_plan'))
      formData.append(`properties[${APPSTLE_PROPS.FULFILLMENT_COUNT}]`, '1')
    }

    const body = useJsonCart ? JSON.stringify(variables) : formData
    fetch(url, { method: 'POST', headers, body })
      .then(response => response.json())
      .then(response => {
        if (response.status) {
          //- broadcast add to cart request failed event
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            errors: response.errors || response.description,
            message: response.message
          })
          //- Sentry report error
          ReportSentry.cartAddError({ variantId: formData.get('id'), quantity: formData.get('quantity'), response })
          this.handleErrorMessage(response.description)
          event.submitter.setAttribute('aria-disabled', true)
          this.error = true
          return
        }
        const eventCategory = event.submitter.getAttribute('data-event-category') || ''
        if (typeof window.WDSendEvent === 'function') {
          window.WDSendEvent('wd_add_to_cart', {
            event_source: window.location.pathname,
            event_category: eventCategory,
            event_action: event.submitter.getAttribute('data-sku') || ''
          })
        }

        if (!this.cart) {
          window.location = (window.routes && window.routes.cart_url) || '/cart'
          return
        }

        //* measure time, performance tracking
        const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers')
        if (!this.error) {
          //- send cart update event
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: response
          }).then(() => {
            CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker)
          })
        }
        this.error = false
        CartPerformance.measure('add:paint-updated-sections', () => {
          this.cart?.renderContents(response)
        })
      })
      .catch(e => {
        ReportSentry.cartAddError({
          variantId: formData.get('id'),
          quantity: formData.get('quantity'),
          error: e,
          isNetworkError: true
        })
        console.error(e)
      })
      .finally(() => {
        event.submitter.classList.remove('loading')
        if (this.submitButtonText) this.submitButtonText.textContent = window.variantStrings.addToCart
        else this.submitButton.textContent = window.variantStrings.addToCart

        event.submitter.blur()

        if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty')
        if (!this.error) event.submitter.removeAttribute('aria-disabled')

        CartPerformance.measureFromEvent('add:user-action', event)

        if (hasFilterSellingPlan) {
          this.reportSubscriptionWdEvent('wd_add_to_cart_subscription', selectedFilterSellingPlans)
        }
      })
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return

    this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form-error-message-wrapper')
    if (!this.errorMessageWrapper) return
    this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form-error-message')

    this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage)

    if (errorMessage) this.errorMessage.textContent = errorMessage
  }

  toggleSubmitButton(disable, text) {
    if (disable) {
      this.submitButton.setAttribute('disabled', 'disabled')
      this.submitButtonText.textContent = text || window.variantStrings.soldOut
      this.buyNowButton.setAttribute('disabled', 'disabled')
      this.buyNowButton.classList.add('hidden')
    } else {
      this.submitButton.removeAttribute('disabled')
      this.submitButtonText.textContent = window.variantStrings.addToCart
      this.buyNowButton.removeAttribute('disabled')
      this.buyNowButton.classList.remove('hidden')
    }
  }

  get variantIdInput() {
    return this.form?.querySelector('[name=id]')
  }
}

class ProductFilterSubscriptionRadio extends HTMLElement {
  constructor() {
    super()
    this.onChangeBound = this.onChange.bind(this)
    this.subscriptionRadio = null
    this.checkboxes = []
  }

  connectedCallback() {
    this.checkboxes = Array.from(this.querySelectorAll('input[name="filter_selling_plan[]"]'))
    this.subscriptionRadio = this.querySelector('input[name="purchase_type"][value="subscription"]')
    this.checkboxes.forEach(cb => {
      cb.disabled = this.isSoldOut(cb)
    })
    if (this.subscriptionRadio) this.subscriptionRadio.disabled = false
    this.addEventListener('change', this.onChangeBound)
    this.applyPurchaseTypeState()
  }

  isSoldOut(cb) {
    return cb.dataset.available === 'false'
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChangeBound)
  }

  onChange(event) {
    const { target } = event
    if (target?.name === 'purchase_type') {
      this.applyPurchaseTypeState()
      return
    }
    if (target?.name !== 'filter_selling_plan[]') return
    if (this.getSelectedPurchaseType() === 'subscription') {
      this.enforceMinimumFilterSelection(target)
    }
  }

  getSelectedPurchaseType() {
    return this.querySelector('input[name="purchase_type"]:checked')?.value
  }

  applyPurchaseTypeState() {
    if (!this.checkboxes.length) return

    if (this.getSelectedPurchaseType() === 'subscription') {
      this.checkboxes.forEach(cb => {
        cb.checked = !this.isSoldOut(cb)
      })
      return
    }

    this.checkboxes.forEach(cb => {
      cb.checked = false
    })
  }

  enforceMinimumFilterSelection(changed) {
    const checked = this.checkboxes.filter(cb => cb.checked)
    if (checked.length === 0 && !this.isSoldOut(changed)) {
      changed.checked = true
      window.showMessage('You must select at least one item.', 'warning', 3000)
    }
  }
}

if (!customElements.get('product-filter-subscription-radio')) {
  customElements.define('product-filter-subscription-radio', ProductFilterSubscriptionRadio)
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm)
}

class ProductInfo extends HTMLElement {
  quantityInput = undefined
  quantityForm = undefined
  onVariantChangeUnSubscriber = undefined
  cartUpdateUnSubscriber = undefined
  abortController = undefined
  pendingRequestUrl = null
  preProcessHtmlCallbacks = []
  postProcessHtmlCallbacks = []

  constructor() {
    super()
    this.quantityInput = this.querySelector('.product-form-quantity--input')
  }

  connectedCallback() {
    this.initializeProductSwapUtility()
    //- subscribe variant change event
    this.onVariantChangeUnSubscriber = subscribe(
      PUB_SUB_EVENTS.optionValueSelectionChange,
      this.handleOptionValueChange.bind(this)
    )

    this.initQuantityHandlers()
    this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }))
  }

  /**
   * @deprecated will be deprecated
   * @param {Function} callback
   */
  addPreProcessCallback(callback) {
    this.preProcessHtmlCallbacks.push(callback)
  }

  initQuantityHandlers() {
    if (!this.quantityInput) return

    this.quantityForm = this.querySelector('.product-form-quantity')
    if (!this.quantityForm) return

    this.setQuantityBoundaries()
  }

  disconnectedCallback() {
    this.onVariantChangeUnSubscriber()
    this.cartUpdateUnSubscriber?.()
  }

  /**
   * @description initialize product swap utility
   */
  initializeProductSwapUtility() {
    this.postProcessHtmlCallbacks.push(_ => {
      window?.Shopify?.PaymentButton?.init()
      window?.ProductModel?.loadShopifyXR()
    })
  }

  /**
   * @description handle variant change
   * @param {Object} event
   */
  handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
    if (!this.contains(event.target)) return

    this.resetProductFormState()

    const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url
    this.pendingRequestUrl = productUrl
    const shouldSwapProduct = this.dataset.url !== productUrl
    const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct
    const selectedValues = target.dataset.updateUrl === 'true' ? [] : selectedOptionValues
    //- re-fetch product info
    this.renderProductInfo({
      requestUrl: this.buildRequestUrlWithParams(productUrl, selectedValues, shouldFetchFullPage),
      targetId: target.id,
      callback: shouldSwapProduct
        ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
        : this.handleUpdateProductInfo(productUrl)
    })
  }

  /**
   * @description reset product form state
   */
  resetProductFormState() {
    const { productForm } = this
    // productForm?.toggleSubmitButton(true)
    productForm?.handleErrorMessage()
  }

  /**
   * @description handle product swap
   * @param {string} productUrl
   * @param {boolean} updateFullPage
   * @returns {Function}
   */
  handleSwapProduct(productUrl, updateFullPage) {
    if (updateFullPage) {
      window.location.href = productUrl
      return null
    }
    window.scrollTo({ top: 0, behavior: 'instant' })
    return html => {
      this.productModal?.remove()

      const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info'
      const variant = this.getSelectedVariant(html.querySelector(selector))
      this.updateURL(productUrl, variant?.id)

      if (updateFullPage) {
        document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML
        document.getElementById('breadcrumb').innerHTML = html.getElementById('breadcrumb').innerHTML

        HTMLUpdateUtility.viewTransition(
          document.querySelector('main'),
          html.querySelector('main'),
          this.preProcessHtmlCallbacks,
          this.postProcessHtmlCallbacks
        )
      } else {
        HTMLUpdateUtility.viewTransition(
          this,
          html.querySelector('product-info'),
          this.preProcessHtmlCallbacks,
          this.postProcessHtmlCallbacks
        )
      }
    }
  }

  renderProductInfo({ requestUrl, targetId, callback }) {
    this.abortController?.abort()
    this.abortController = new AbortController()

    fetch(requestUrl, { signal: this.abortController.signal })
      .then(response => response.text())
      .then(responseText => {
        this.pendingRequestUrl = null
        const html = new DOMParser().parseFromString(responseText, 'text/html')
        callback(html)
      })
      .then(() => {
        // set focus to last clicked option value
        document.querySelector(`#${targetId}`)?.focus()
      })
      .catch(error => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user')
        } else {
          console.error(error)
        }
      })
  }

  getSelectedVariant(productInfoNode) {
    const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML
    return selectedVariant ? JSON.parse(selectedVariant) : null
  }

  buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
    const params = []

    !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`)

    if (optionValues.length) {
      params.push(`option_values=${optionValues.join(',')}`)
    }

    return `${url}?${params.join('&')}`
  }

  updateOptionValues(html) {
    const variantSelects = html.querySelector('variant-selects')
    if (variantSelects) {
      HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks)
    }
  }

  /**
   * update product info
   * @param {string} productUrl
   */
  handleUpdateProductInfo(productUrl) {
    return html => {
      const variant = this.getSelectedVariant(html)

      this.pickupAvailability?.update(variant)
      this.updateOptionValues(html)
      this.updateURL(productUrl, variant?.id)
      this.updateVariantInputs(variant?.id)

      if (!variant) {
        this.setUnavailable()
        return
      }

      //- update media carousel
      this.updateMedia(variant.featured_media?.id)

      //- update price
      const updateSourceFromDestination = (id, shouldHide = _ => false) => {
        const source = html.getElementById(`${id}-${this.sectionId}`)
        const destination = this.querySelector(`#${id}-${this.dataset.section}`)
        if (source && destination) {
          destination.innerHTML = source.innerHTML
          destination.classList.toggle('hidden', shouldHide(source))
        }
      }
      updateSourceFromDestination('price')
      updateSourceFromDestination('price-info-thumbnail-tag')
      updateSourceFromDestination('price-bottom')
      updateSourceFromDestination('link-picker')
      updateSourceFromDestination('buy-buttons-bottom')
      updateSourceFromDestination('bottom-fixed-left')
      updateSourceFromDestination('product-info-gifts')
      updateSourceFromDestination('member-points')
      updateSourceFromDestination('member-price')
      updateSourceFromDestination('component-subscription-radio')
      updateSourceFromDestination('product-variant-info')
      //- update quantity rules
      this.updateQuantityRules(this.sectionId, html)
      this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden')

      //- update submit button
      this.productForm?.toggleSubmitButton(
        html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
        window.variantStrings.soldOut
      )

      //- broadcast variant change event
      publish(PUB_SUB_EVENTS.variantChange, { data: { sectionId: this.sectionId, html, variant } })
    }
  }

  /**
   * update variant inputs
   * @param {string} variantId
   */
  updateVariantInputs(variantId) {
    this.querySelectorAll(`#product-form-${this.dataset.section}`).forEach(productForm => {
      const input = productForm.querySelector('input[name="id"]')
      if (input) {
        input.value = variantId ?? ''
        input.dispatchEvent(new Event('change', { bubbles: true }))
      }
    })
  }

  /**
   * update URL
   * @param {string} url
   * @param {string} variantId
   */
  updateURL(url, variantId) {
    if (this.dataset.updateUrl === 'false') return
    window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`)
  }

  setUnavailable() {
    this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable)

    const selectors = ['price', 'Quantity-Rules'].map(id => `#${id}-${this.dataset.section}`).join(', ')
    document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'))
  }

  /**
   * @description update media carousel
   * @param {string} variantFeaturedMediaId
   */
  updateMedia(variantFeaturedMediaId) {
    if (!variantFeaturedMediaId) return
    const mediaCarousel = document.getElementById(`MediaCarousel-${this.dataset.section}`)
    mediaCarousel.setActiveMedia(`${this.dataset.section}-${variantFeaturedMediaId}`)
  }

  /**
   * @description set quantity boundaries
   */
  setQuantityBoundaries() {
    const data = {
      cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
      min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
      max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
      step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1
    }

    let { min } = data
    const max = data.max === null ? data.max : data.max - data.cartQuantity
    if (max !== null) min = Math.min(min, max)
    if (data.cartQuantity >= data.min) min = Math.min(min, data.step)

    this.quantityInput.min = min

    if (max) this.quantityInput.max = max
    else this.quantityInput.removeAttribute('max')

    this.quantityInput.value = min

    publish(PUB_SUB_EVENTS.quantityRulesUpdate, undefined)
  }

  /**
   * @description update quantity rules
   * @param {string} sectionId
   * @param {Document} html
   */
  updateQuantityRules(sectionId, html) {
    if (!this.quantityInput) return
    this.setQuantityBoundaries()

    const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`)
    if (!quantityFormUpdated) return
    const soldOutAttribute = quantityFormUpdated.getAttribute('data-sold-out')
    if (soldOutAttribute !== null) this.quantityForm.setAttribute('data-sold-out', 'true')
    else this.quantityForm.removeAttribute('data-sold-out')

    const selectors = ['.product-form-quantity--input', '.quantity__rules']
    for (const selector of selectors) {
      const current = this.quantityForm.querySelector(selector)
      const updated = quantityFormUpdated.querySelector(selector)
      if (!current || !updated) continue
      if (selector === '.product-form-quantity--input') {
        const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step']
        for (const attribute of attributes) {
          const valueUpdated = updated.getAttribute(attribute)
          if (valueUpdated !== null) {
            current.setAttribute(attribute, valueUpdated)
          } else {
            current.removeAttribute(attribute)
          }
        }
      }
    }
  }

  get productForm() {
    return this.querySelector(`product-form`)
  }

  get productModal() {
    return document.querySelector(`#ProductModal-${this.dataset.section}`)
  }

  get pickupAvailability() {
    return this.querySelector(`pickup-availability`)
  }

  get variantSelectors() {
    return this.querySelector('variant-selects')
  }

  get relatedProducts() {
    const relatedProductsSectionId = SectionId.getIdForSection(SectionId.parseId(this.sectionId), 'related-products')
    return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`)
  }

  get quickOrderList() {
    const quickOrderListSectionId = SectionId.getIdForSection(SectionId.parseId(this.sectionId), 'quick_order_list')
    return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`)
  }

  get sectionId() {
    return this.dataset.originalSection || this.dataset.section
  }
}

if (!customElements.get('product-info')) {
  customElements.define('product-info', ProductInfo)
}

/**
 * @description <variant-selects>
 */
class VariantSelects extends HTMLElement {
  constructor() {
    super()
  }

  connectedCallback() {
    this.addEventListener('change', event => {
      this.updateSelectionMetadata(event)

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target: event.target,
          selectedOptionValues: this.selectedOptionValues
        }
      })
    })
  }

  /**
   * @description update selector display state
   * @param {Event} event 事件对象
   */
  updateSelectionMetadata({ target }) {
    if (target.tagName === 'INPUT' && target.type === 'radio') {
      const pickers = this.querySelectorAll(`[id^="${target.dataset.variantPickerId}"]`)
      if (pickers) {
        pickers.forEach(picker => {
          picker.removeAttribute('checked')
        })
      }
      target.parentElement.setAttribute('checked', '')
    }
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    )
  }
}
if (!customElements.get('variant-selects')) {
  customElements.define('variant-selects', VariantSelects)
}

/**
 * @description <quantity-input>
 */
class QuantityInput extends HTMLElement {
  constructor() {
    super()
    this.input = this.querySelector('input')
    this.changeEvent = new Event('change', { bubbles: true })
    this.input?.addEventListener('change', this.onInputChange.bind(this))
    this.input?.addEventListener('blur', this.onInputBlur.bind(this))
    this.querySelectorAll('button').forEach(button => button.addEventListener('click', this.onButtonClick.bind(this)))
    this.addButton = null
    this.buyNowButton = null
    this.addButtonSM = null
  }

  quantityUpdateUnSubscriber = undefined

  connectedCallback() {
    this.validateQtyRules()
    this.quantityUpdateUnSubscriber = subscribe(PUB_SUB_EVENTS.quantityRulesUpdate, this.validateQtyRules.bind(this))
    this.addButton = document.querySelector('.product-info-bottom-add-to-cart-button')
    this.buyNowButton = document.querySelector('.product-info-bottom-buy-now-button')
    this.addButtonSM = document.querySelector('.product-info-bottom-add-to-cart-button-sm')
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnSubscriber) {
      this.quantityUpdateUnSubscriber()
    }
  }

  onInputChange() {
    this.validateQtyRules()
  }

  onInputBlur() {
    const value = parseInt(this.input.value)
    const min = parseInt(this.input.dataset.min) ?? 1
    const max = parseInt(this.input.dataset.max) ?? Infinity
    if (!value || isNaN(value) || value < 0) {
      this.input.value = min
      return
    }
    if (value < min) this.input.value = min
    else if (value > max) this.input.value = max
  }

  onButtonClick(event) {
    event.preventDefault()
    const previousValue = this.input.value
    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value === 0) {
        this.input.value = this.input.dataset.min
      } else {
        this.input.stepUp()
      }
    } else {
      this.input.stepDown()
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent)

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min)
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value)
    if (this.input.min) {
      const buttonMinus = this.querySelector(".product-form-quantity--button[name='minus']")
      buttonMinus?.classList.toggle(
        'product-form-quantity--button-disabled',
        parseInt(value) <= parseInt(this.input.min)
      )
    }
    if (this.input.max) {
      const max = parseInt(this.input.max)
      const buttonPlus = this.querySelector(".product-form-quantity--button[name='plus']")
      buttonPlus?.classList.toggle('product-form-quantity--button-disabled', value >= max)
    }
  }
}
if (!customElements.get('quantity-input')) {
  customElements.define('quantity-input', QuantityInput)
}

/**
 * @description <product-buy-buttons>
 * listen to own visibility, control bottom fixed display/hide
 */
class ProductBuyButtons extends HTMLElement {
  constructor() {
    super()
    this.observer = null
    this.bottomFixed = null
    this.sectionId = null
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId || this.id.replace('ProductForm-BuyButtons-', '')

    if (!this.sectionId) return

    this.bottomFixed = document.getElementById(`BottomFixed-${this.sectionId}`)

    if (!this.bottomFixed) {
      this.waitForBottomFixed()
      return
    }

    this.initObserver()
    this.checkInitialVisibility()
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  }

  /**
   * @description wait for bottom fixed button to load
   */
  waitForBottomFixed() {
    const checkBottomFixed = () => {
      this.bottomFixed = document.getElementById(`BottomFixed-${this.sectionId}`)
      if (this.bottomFixed) {
        this.initObserver()
        this.checkInitialVisibility()
      } else {
        setTimeout(checkBottomFixed, 100)
      }
    }

    //- delay check, ensure DOM is fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', checkBottomFixed)
    } else {
      setTimeout(checkBottomFixed, 100)
    }
  }

  /**
   * @description initialize observer
   */
  initObserver() {
    if (!this.bottomFixed || this.observer) return

    this.observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          this.bottomFixed.classList.toggle('product-info-bottom-add-to-cart-hidden', entry.isIntersecting)
        })
      },
      { threshold: 0.1, rootMargin: '0px' }
    )

    this.observer.observe(this)
  }

  /**
   * @description check initial visibility state
   */
  checkInitialVisibility() {
    if (!this.bottomFixed) return

    const rect = this.getBoundingClientRect()
    const isVisible = rect.top < window.innerHeight && rect.bottom > 0
    this.bottomFixed.classList.toggle('product-info-bottom-add-to-cart-hidden', isVisible)
  }
}
if (!customElements.get('product-buy-buttons')) {
  customElements.define('product-buy-buttons', ProductBuyButtons)
}

class ProductInfoGift extends HTMLElement {
  static endSoonThreshold = 3 * 60 * 1000
  static dateRegex = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?$/

  connectedCallback() {
    this.updateEndDateText()
    this.updateVisibility()
  }

  disconnectedCallback() {
    clearTimeout(this.endTimer)
  }

  parseLocalDate(value) {
    if (!value) return NaN
    const match = String(value).trim().match(ProductInfoGift.dateRegex)
    if (!match) return NaN
    const year = Number(match[1])
    const month = Number(match[2]) - 1
    const day = Number(match[3])
    const hour = Number(match[4] || 0)
    const minute = Number(match[5] || 0)
    const second = Number(match[6] || 0)
    return new Date(year, month, day, hour, minute, second).getTime()
  }

  getRange() {
    const startTime = this.parseLocalDate(this.dataset.startDate)
    const endTime = this.parseLocalDate(this.dataset.endDate)
    return {
      startTime: Number.isNaN(startTime) ? -Infinity : startTime,
      endTime: Number.isNaN(endTime) ? Infinity : endTime
    }
  }

  formatDate(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${month}/${day}/${year}`
  }

  updateEndDateText() {
    const endEl = this.querySelector('.product-info-free-gift-end')
    const endTime = this.parseLocalDate(this.dataset.endDate)
    if (!endEl || Number.isNaN(endTime)) return
    endEl.textContent = `Ends ${this.formatDate(new Date(endTime))}`
  }

  updateVisibility() {
    const { startTime, endTime } = this.getRange()
    const now = Date.now()
    const shouldHide = now < startTime || now > endTime
    this.classList.toggle('hidden', shouldHide)

    clearTimeout(this.endTimer)

    if (endTime === Infinity || shouldHide) return
    const delay = endTime + 1 - now
    if (delay > 0 && delay <= ProductInfoGift.endSoonThreshold) {
      this.endTimer = setTimeout(() => this.updateVisibility(), delay)
    }
  }
}

if (!customElements.get('product-info-gift')) {
  customElements.define('product-info-gift', ProductInfoGift)
}
